import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { CustodyShield } from "@/components/CustodyShield";
import { LegalDisclaimer } from "@/context/DisclaimerContext";
import { AcknowledgeReceipt } from "@/components/AcknowledgeReceipt";
import { RecipientResponse } from "@/components/RecipientResponse";
import { TamperEvidentBadge } from "@/components/TamperEvident";
import { Loader2, MapPin, DoorOpen, X, ChevronLeft, ChevronRight, ShieldCheck } from "lucide-react";

const CLASS_META = {
  NO_CHANGE: { label: "No Change", cls: "bg-[#D1FAE5] text-[#177245] border-[#A7F3D0]" },
  NORMAL_WEAR: { label: "Normal Wear", cls: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" },
  NEW_DAMAGE: { label: "New Damage", cls: "bg-[#FEE2E2] text-[#B91C1C] border-[#FECACA]" },
};
const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "");
const photoDateLabel = (ph) => (ph.original_capture_date ? `Taken ${fmt(ph.original_capture_date)} · Uploaded ${fmt(ph.created_at)}` : `${fmt(ph.created_at)}`);

function Img({ src, alt, onClick }) {
  const [err, setErr] = useState(false);
  if (err) return <div className="aspect-video grid place-items-center rounded-lg border border-[#E2E8F0] bg-[#F1F5F9] text-xs text-[#94A3B8]">Photo unavailable</div>;
  return <img src={`${API}${src}`} alt={alt} onError={() => setErr(true)} onClick={onClick}
    className="aspect-video w-full object-cover rounded-lg border border-[#E2E8F0] cursor-zoom-in" />;
}

export default function PublicSlugReport() {
  const { slug } = useParams();
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);
  const [lightbox, setLightbox] = useState(null); // {images:[], idx}

  useEffect(() => {
    document.title = "Property Condition Report";
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow";
    document.head.appendChild(m);
    axios.get(`${API}/report/${slug}`)
      .then(({ data }) => { setData(data); setState("ok"); })
      .catch(() => setState("notfound"));
    return () => { document.head.removeChild(m); };
  }, [slug]);

  const openLb = (images, idx) => setLightbox({ images, idx });
  const move = (d) => setLightbox((lb) => ({ ...lb, idx: (lb.idx + d + lb.images.length) % lb.images.length }));

  if (state === "loading") return <div className="min-h-screen bg-[#EEF2F6] p-6"><div className="max-w-3xl mx-auto space-y-4"><div className="h-10 w-48 bg-[#E2E8F0] rounded animate-pulse" /><div className="h-64 bg-[#E2E8F0] rounded-2xl animate-pulse" /></div></div>;
  if (state === "notfound") return (
    <div className="min-h-screen grid place-items-center bg-[#EEF2F6] px-6 text-center" data-testid="report-notfound">
      <div><h1 className="font-heading text-2xl font-bold text-navy">This report link is invalid or no longer available.</h1></div>
    </div>
  );

  const p = data.property;
  const s = data.summary || {};

  return (
    <div className="min-h-screen bg-[#EEF2F6]">
      <header className="border-b border-[#E2E8F0] bg-white/90 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
          <Logo />
          <h1 className="mt-3 font-heading text-2xl font-bold text-navy">Property Condition Report</h1>
          <p className="text-sm text-[#475569] flex items-center gap-1.5"><MapPin size={14} className="text-brand" /> {p.address}</p>
          <p data-testid="public-stage-line" className="mt-1.5 text-sm text-[#475569]">
            {p.documented_stage === "mid_lease"
              ? `Condition as of ${fmt(p.created_at)} — documentation prepared with TenantSeal.`
              : `${p.doc_type === "move_in" ? "Move-in" : "Move-out"} documentation prepared with TenantSeal.`}
          </p>
          {p.is_locked && p.manifest_hash && (
            <div className="mt-2 flex items-center gap-2 text-xs text-[#94A3B8]">
              {p.locked_at && <span>Locked {fmt(p.locked_at)}</span>}
              <TamperEvidentBadge manifestHash={p.manifest_hash} lockedAt={p.locked_at} testId="public-tamper-evident-badge" />
            </div>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6" data-testid="public-slug-report">
        <p className="mb-4 text-xs text-[#64748B] bg-white border border-[#E2E8F0] rounded-lg px-3 py-2" data-testid="public-respond-hint">
          Anyone with this link can view and respond to this shared TenantSeal report.
        </p>
        {/* Summary */}
        <div className="flex flex-wrap gap-2" data-testid="report-summary">
          <Pill n={s.NEW_DAMAGE || 0} label="New Damage" cls={CLASS_META.NEW_DAMAGE.cls} />
          <Pill n={s.NORMAL_WEAR || 0} label="Normal Wear" cls={CLASS_META.NORMAL_WEAR.cls} />
          <Pill n={s.NO_CHANGE || 0} label="No Change" cls={CLASS_META.NO_CHANGE.cls} />
        </div>

        {/* Comparison findings — room by room */}
        {(data.comparisons || []).length > 0 && (
          <div className="mt-6 space-y-5">
            <LegalDisclaimer keyName="ai_analysis" className="text-[11px] leading-relaxed text-[#94A3B8]" />
            {data.comparisons.map((c, i) => {
              const meta = CLASS_META[c.classification] || CLASS_META.NO_CHANGE;
              const imgs = [{ src: c.move_in_image, label: `Move-In · ${fmt(c.move_in_date)}`, redacted: c.move_in_redacted }, { src: c.move_out_image, label: `Move-Out · ${fmt(c.move_out_date)}`, redacted: c.move_out_redacted }];
              const shownImgs = imgs.filter((im) => im.src);
              return (
                <div key={c.room_label} data-testid={`report-comparison-${i}`} className="rounded-2xl border border-[#E2E8F0] bg-white p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-heading font-semibold text-navy flex items-center gap-2"><DoorOpen size={16} className="text-brand" /> {c.room_label}</span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${meta.cls}`}>{meta.label}</span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {imgs.map((im, j) => (
                      <div key={im.label} className="relative">
                        {im.src ? (
                          <Img src={im.src} alt={im.label} onClick={() => openLb(shownImgs, shownImgs.findIndex((s) => s.src === im.src))} />
                        ) : (
                          <div className="aspect-video rounded-lg bg-[#F1F5F9] border border-dashed border-[#CBD5E1] flex items-center justify-center text-[11px] text-[#94A3B8]" data-testid={`comparison-img-hidden-${i}-${j}`}>Not shared</div>
                        )}
                        {j === 1 && c.move_out_custody && <CustodyShield custody={c.move_out_custody} className="absolute top-2 right-2 z-10" />}
                        <p className="mt-1 text-[11px] text-[#94A3B8]">{im.label}</p>
                        {im.src && im.redacted && <p className="text-[10px] font-medium text-[#7C3AED]">Redacted share copy created by the TenantSeal user.</p>}
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 text-sm">
                    {c.confidence != null && <span className="text-[#475569]">Confidence: <b className="text-navy">{Math.round(c.confidence * 100)}%</b></span>}
                    {c.damage_description && <p className="mt-1 text-[#334155]">{c.damage_description}</p>}
                    {(c.affected_regions || []).length > 0 && (
                      <ul className="mt-2 list-disc list-inside text-[#334155]">
                        {c.affected_regions.map((r, k) => <li key={`${r.label}-${r.location_description || k}`}><b>{r.label}</b>{r.location_description ? ` — ${r.location_description}` : ""}</li>)}
                      </ul>
                    )}
                  </div>
                  <RecipientResponse slug={slug} itemRef={`comparison:${c.comparison_id}`} itemLabel={`${c.room_label} (comparison)`} testId={`comparison-response-${i}`} />
                </div>
              );
            })}
          </div>
        )}

        {/* Photo gallery by room */}
        {(data.rooms || []).some((r) => r.photos.length > 0) && (
          <div className="mt-8">
            <h2 className="font-heading text-lg font-semibold text-navy">All photos</h2>
            {data.rooms.filter((r) => r.photos.length > 0).map((r, i) => {
              const imgs = r.photos.map((ph) => ({ src: ph.image_path, label: `${r.name} · ${photoDateLabel(ph)}` }));
              return (
                <div key={r.name} className="mt-4" data-testid={`report-room-${i}`}>
                  <p className="text-sm font-medium text-navy">{r.name}</p>
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {r.photos.map((ph, j) => (
                      <div key={ph.image_path} className="relative">
                        <Img src={ph.image_path} alt={`${r.name} · ${photoDateLabel(ph)}`} onClick={() => openLb(imgs, j)} />
                        {ph.custody && <CustodyShield custody={ph.custody} className="absolute top-2 right-2 z-10" />}
                        <p className="mt-1 text-[11px] text-[#64748B]" data-testid={`photo-date-${i}-${j}`}>{photoDateLabel(ph)}</p>
                        {ph.redacted && (
                          <p data-testid={`redaction-label-${i}-${j}`} className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-[#7C3AED]">
                            <ShieldCheck size={11} /> {ph.redaction_label}
                          </p>
                        )}
                        {ph.fingerprint && (
                          <a href={`/verify/${ph.fingerprint}`} target="_blank" rel="noreferrer"
                             data-testid={`verify-photo-link-${i}-${j}`}
                             className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-brand hover:underline">
                            <ShieldCheck size={11} /> Verify this photo
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                  <RecipientResponse slug={slug} itemRef={`room:${r.room_id}`} itemLabel={r.name} testId={`room-response-${i}`} />
                </div>
              );
            })}
          </div>
        )}

        {data.tenant_attestation && (
          <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-6" data-testid="public-tenant-attestation">
            <h2 className="font-heading text-lg font-semibold text-navy">Tenant attestation</h2>
            <p className="mt-2 text-sm text-navy">&ldquo;{data.tenant_attestation.statement_text}&rdquo;</p>
            <div className="mt-2 text-xs text-[#64748B] space-y-0.5">
              <div>Signed by {data.tenant_attestation.signer_name} · {new Date(data.tenant_attestation.signed_at).toLocaleString()} · Statement {data.tenant_attestation.statement_version}</div>
              <div>Covers evidence types: {(data.tenant_attestation.covered_evidence_types || []).join(", ") || "—"}</div>
              <div className="font-mono break-all">Bound seal fingerprint: {data.tenant_attestation.seal_fingerprint}</div>
            </div>
            <p className="mt-2 text-[11px] text-[#94A3B8]">
              This is the tenant's own attestation — not notarization, identity verification, landlord agreement, or proof the underlying events are true.
            </p>
          </div>
        )}

        <AcknowledgeReceipt endpoint={`/report/${slug}/acknowledge`} initialAcknowledgedAt={data.acknowledged_at} />

        <footer className="mt-10 py-6 text-center">
          <a href="/" className="text-sm text-[#94A3B8] hover:text-brand">Prepared with TenantSeal</a>
        </footer>
      </main>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/85 grid place-items-center p-4" data-testid="lightbox" onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={() => setLightbox(null)}><X size={28} /></button>
          <button className="absolute left-3 text-white/80 hover:text-white" onClick={(e) => { e.stopPropagation(); move(-1); }}><ChevronLeft size={32} /></button>
          <img src={`${API}${lightbox.images[lightbox.idx].src}`} alt="" className="max-h-[85vh] max-w-full rounded-lg" onClick={(e) => e.stopPropagation()} />
          <button className="absolute right-3 text-white/80 hover:text-white" onClick={(e) => { e.stopPropagation(); move(1); }}><ChevronRight size={32} /></button>
          <p className="absolute bottom-4 text-white/80 text-sm">{lightbox.images[lightbox.idx].label}</p>
        </div>
      )}
    </div>
  );
}

function Pill({ n, label, cls }) {
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${cls}`}>{n} {label}</span>;
}
